import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { errorMessage, publicApi, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import { webDevice } from "../auth/device";
import { safeNext } from "../auth/next";
import { AuthLayout } from "../components/AuthLayout";

const MIN_PASSWORD = 10;

export function SignupPage() {
  const { session, signIn } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (session) return <Navigate to={next} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await signIn(
        unwrap(await publicApi.POST("/auth/signup", { body: { name, email, password, device: webDevice() } })),
      );
    } catch (err) {
      setError(errorMessage(err, { 409: "An account with this email already exists." }));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout title="Create an account">
      <form onSubmit={(event) => void submit(event)} className="stack">
        <label>
          Name
          <input
            autoComplete="name"
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD}
            maxLength={256}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span className="hint">At least {MIN_PASSWORD} characters.</span>
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending}>
          Create account
        </button>
      </form>
      <p className="muted">
        Already have an account? <Link to={`/login${params.size ? `?${params.toString()}` : ""}`}>Sign in</Link>
      </p>
    </AuthLayout>
  );
}
